import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as automationApi from '../../api/automationApi';

// Async thunks
export const fetchAutomations = createAsyncThunk(
  'automations/fetchAutomations',
  async (boardId, { rejectWithValue }) => {
    try {
      return await automationApi.fetchAutomations(boardId);
    } catch (error) {
      return rejectWithValue({ message: error.message || 'Failed to fetch automations' });
    }
  }
);

export const fetchAutomationOptions = createAsyncThunk(
  'automations/fetchAutomationOptions',
  async (boardId, { rejectWithValue }) => {
    try {
      return await automationApi.fetchAutomationOptions(boardId);
    } catch (error) {
      return rejectWithValue({ message: error.message || 'Failed to fetch options' });
    }
  }
);

export const createAutomation = createAsyncThunk(
  'automations/createAutomation',
  async ({ boardId, data }, { rejectWithValue }) => {
    try {
      return await automationApi.createAutomation(boardId, data);
    } catch (error) {
      return rejectWithValue({ message: error.message || 'Failed to create automation' });
    }
  }
);

export const updateAutomation = createAsyncThunk(
  'automations/updateAutomation',
  async ({ boardId, automationId, data }, { rejectWithValue }) => {
    try {
      return await automationApi.updateAutomation(boardId, automationId, data);
    } catch (error) {
      return rejectWithValue({ message: error.message || 'Failed to update automation' });
    }
  }
);

export const deleteAutomation = createAsyncThunk(
  'automations/deleteAutomation',
  async ({ boardId, automationId }, { rejectWithValue }) => {
    try {
      await automationApi.deleteAutomation(boardId, automationId);
      return automationId;
    } catch (error) {
      return rejectWithValue({ message: error.message || 'Failed to delete automation' });
    }
  }
);

export const toggleAutomation = createAsyncThunk(
  'automations/toggleAutomation',
  async ({ boardId, automationId }, { rejectWithValue }) => {
    try {
      return await automationApi.toggleAutomation(boardId, automationId);
    } catch (error) {
      return rejectWithValue({ message: error.message || 'Failed to toggle automation' });
    }
  }
);

export const fetchAutomationLogs = createAsyncThunk(
  'automations/fetchAutomationLogs',
  async ({ boardId, automationId, page, limit }, { rejectWithValue }) => {
    try {
      return await automationApi.fetchAutomationLogs(boardId, automationId, page, limit);
    } catch (error) {
      return rejectWithValue({ message: error.message || 'Failed to fetch logs' });
    }
  }
);

const automationSlice = createSlice({
  name: 'automations',
  initialState: {
    automations: [],
    automationUsage: null, // { used, limit, periodStart, planType }
    options: null,
    logs: { logs: [], total: 0, page: 1, totalPages: 0 },
    loading: true,
    optionsLoading: false,
    logsLoading: false,
    error: null
  },
  reducers: {
    clearAutomations: (state) => {
      state.automations = [];
      state.loading = true;
      state.error = null;
    },
    clearLogs: (state) => {
      state.logs = { logs: [], total: 0, page: 1, totalPages: 0 };
    },
    // Handle real-time updates from Socket.IO
    automationCreatedRealtime: (state, action) => {
      const exists = state.automations.find(a => a._id === action.payload._id);
      if (!exists) {
        state.automations.unshift(action.payload);
      }
    },
    automationUpdatedRealtime: (state, action) => {
      const index = state.automations.findIndex(a => a._id === action.payload._id);
      if (index !== -1) {
        state.automations[index] = action.payload;
      }
    },
    automationDeletedRealtime: (state, action) => {
      state.automations = state.automations.filter(a => a._id !== action.payload);
    },
    automationToggledRealtime: (state, action) => {
      const index = state.automations.findIndex(a => a._id === action.payload._id);
      if (index !== -1) {
        state.automations[index] = action.payload;
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch automations
      .addCase(fetchAutomations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAutomations.fulfilled, (state, action) => {
        state.loading = false;
        // Handle new response shape: { automations, automationUsage }
        if (action.payload?.automations) {
          state.automations = action.payload.automations;
          state.automationUsage = action.payload.automationUsage || null;
        } else {
          // Backward compatibility: plain array response
          state.automations = action.payload;
        }
      })
      .addCase(fetchAutomations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to fetch automations';
      })

      // Fetch options
      .addCase(fetchAutomationOptions.pending, (state) => {
        state.optionsLoading = true;
      })
      .addCase(fetchAutomationOptions.fulfilled, (state, action) => {
        state.optionsLoading = false;
        state.options = action.payload;
      })
      .addCase(fetchAutomationOptions.rejected, (state) => {
        state.optionsLoading = false;
      })

      // Create automation
      .addCase(createAutomation.fulfilled, (state, action) => {
        state.automations.unshift(action.payload);
      })

      // Update automation
      .addCase(updateAutomation.fulfilled, (state, action) => {
        const index = state.automations.findIndex(a => a._id === action.payload._id);
        if (index !== -1) {
          state.automations[index] = action.payload;
        }
      })

      // Delete automation
      .addCase(deleteAutomation.fulfilled, (state, action) => {
        state.automations = state.automations.filter(a => a._id !== action.payload);
      })

      // Toggle automation
      .addCase(toggleAutomation.fulfilled, (state, action) => {
        const index = state.automations.findIndex(a => a._id === action.payload._id);
        if (index !== -1) {
          state.automations[index] = action.payload;
        }
      })

      // Fetch logs
      .addCase(fetchAutomationLogs.pending, (state) => {
        state.logsLoading = true;
      })
      .addCase(fetchAutomationLogs.fulfilled, (state, action) => {
        state.logsLoading = false;
        state.logs = action.payload;
      })
      .addCase(fetchAutomationLogs.rejected, (state) => {
        state.logsLoading = false;
      });
  }
});

export const {
  clearAutomations,
  clearLogs,
  automationCreatedRealtime,
  automationUpdatedRealtime,
  automationDeletedRealtime,
  automationToggledRealtime
} = automationSlice.actions;

export default automationSlice.reducer;
